// SubscriptionPicker — inline `<select>` that picks a user's subscription by
// PRODUCT (PX-204): Free / VIP weekly / VIP monthly / VIP quarterly, sourced
// from `/admin/catalog/subscription-products`. Picking a SKU sends only
// `product_id` (the backend derives plan_id from the product — it's
// authoritative for the tier); picking Free sends `{ plan_id: free, unset_product_id }`.
// Shared between the Users list and the UserDetail page so the option labels +
// the Free→null mapping live in one place.

import type {
  AdminPlanResponse,
  AdminSubscriptionProductResponse,
  AdminUserUpdate,
} from '@/types/api'

const FREE_VALUE = '__free__'

export interface SubscriptionChange {
  /** PATCH fragment to send to /admin/users/{id}. */
  patch: AdminUserUpdate
  /** Human label of the new selection (for the confirm modal). */
  label: string
}

/** Human label for a (product_id, plan_id) pair — "Pinxie VIP · month" / "Free"
 *  / "<plan> (no SKU)" for a tier set without a product. */
export function subscriptionLabel(
  productId: string | null,
  planId: number,
  products: AdminSubscriptionProductResponse[],
  plans: AdminPlanResponse[],
): string {
  if (productId) {
    const product = products.find((p) => p.revenuecat_product_id === productId)
    if (!product) return `${productId} (unknown)`
    const planName = plans.find((p) => p.id === product.plan_id)?.name ?? 'Plan'
    return `${planName} · ${product.period}`
  }
  const plan = plans.find((p) => p.id === planId)
  if (!plan || plan.slug === 'free') return 'Free'
  return `${plan.name} (no SKU)`
}

export function SubscriptionPicker({
  productId,
  planId,
  products,
  plans,
  onChange,
  disabled,
}: {
  productId: string | null
  planId: number
  products: AdminSubscriptionProductResponse[]
  plans: AdminPlanResponse[]
  onChange: (change: SubscriptionChange) => void
  disabled?: boolean
}) {
  const freePlanId = plans.find((p) => p.slug === 'free')?.id ?? 1
  const planName = (id: number) => plans.find((p) => p.id === id)?.name ?? `plan #${id}`

  // The select value is keyed by product_id. Two NULL-product cases:
  //  - free plan → "Free"
  //  - paid plan without a SKU (legacy / RC-without-product) → a synthetic
  //    current-state option so the dropdown doesn't misrepresent it as Free.
  const noSkuValue = `__plan_${planId}__`
  const currentValue = productId ?? (planId === freePlanId ? FREE_VALUE : noSkuValue)

  // Active products + the current one even if deactivated — never silently drop
  // the user's existing assignment.
  const visible = products.filter((p) => p.is_active || p.revenuecat_product_id === productId)

  const palette = productId
    ? 'border-brand-300 bg-brand-50 text-brand-700'
    : planId === freePlanId
      ? 'border-ink-200 bg-white text-ink-700'
      : 'border-amber-300 bg-amber-50 text-amber-800'

  const handle = (value: string) => {
    if (value === currentValue) return // includes the synthetic no-SKU option = no-op
    if (value === FREE_VALUE) {
      onChange({ patch: { plan_id: freePlanId, unset_product_id: true }, label: 'Free' })
      return
    }
    const product = products.find((p) => p.revenuecat_product_id === value)
    if (!product) return
    onChange({
      patch: { product_id: value },
      label: `${planName(product.plan_id)} · ${product.period}`,
    })
  }

  return (
    <select
      value={currentValue}
      onChange={(e) => handle(e.target.value)}
      disabled={disabled}
      className={`rounded-md border px-2 py-1 text-xs font-medium transition focus:outline-none focus:ring-2 focus:ring-brand-100 ${palette} disabled:cursor-not-allowed disabled:opacity-60`}
    >
      <option value={FREE_VALUE}>Free</option>
      {/* Current state is a paid tier with no SKU — show it so the dropdown
          reflects reality; re-selecting it is a no-op. */}
      {!productId && planId !== freePlanId && (
        <option value={noSkuValue}>{planName(planId)} (no SKU)</option>
      )}
      {/* Current product_id missing from the catalog (deleted SKU). */}
      {productId && !products.some((p) => p.revenuecat_product_id === productId) && (
        <option value={productId}>{productId} (unknown)</option>
      )}
      {visible.map((p) => (
        <option key={p.id} value={p.revenuecat_product_id}>
          {planName(p.plan_id)} · {p.period}{!p.is_active ? ' · inactive' : ''}
        </option>
      ))}
    </select>
  )
}
